import { find, map } from 'lodash'

import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { WPLink, WPPageData } from '@/types'

export const PostType = {
  POST: 'post'
}

export const normalizeWPLink = (
  type: string,
  link: WPLink,
  labels: Record<string, string>
) => {
  const linkLabelsMap = {
    [PostType.POST]: labels?.read_more
  }

  const title = linkLabelsMap[type]

  return (
    (type && link
      ? title
        ? {
            title,
            url: link,
            target: ''
          }
        : null
      : link) || null
  )
}

export const normalizeWPTaxonomies = (type: string, taxonomies: any[]) => {
  const taxonomiesMap = {
    [PostType.POST]: 'category'
  }

  const taxonomy = taxonomiesMap[type] as any

  return type && taxonomy
    ? map(taxonomies ? taxonomies[taxonomy] : [], 'name').join(', ')
    : null
}

export const normalizePostsCollection = (
  collection: any[],
  { labels }: any
) => {
  return map(
    collection,
    ({
      type,
      color,
      overline,
      image,
      featured_image,
      title_component,
      title_variant,
      title,
      paragraph,
      content,
      taxonomies,
      link
    }) => {
      return {
        type: type || 'post',
        color: color || '#7d3bed',
        image: featured_image || image || null,
        overline: overline || normalizeWPTaxonomies(type, taxonomies),
        title_component: title_component || 'h6',
        title_variant: title_variant || 'h6',
        title: title?.rendered || title || null,
        paragraph: content?.rendered || paragraph || null,
        link: normalizeWPLink(type, link, labels)
      }
    }
  )
}

export const normalizeRelationalSection = (section: any, pageData: any) => {
  const type = section.collection_type
  const collection = section[type] || section.collection

  return {
    ...section,
    collection: normalizePostsCollection(collection, pageData)
  }
}

export const normalizeRelationalArchive = async (
  section: any,
  pageData: any
) => ({
  ...(await normalizeRelationalSection(section, pageData))
})

export const normalizeYoastGraph = (pageData: any) => {
  const schema = pageData.yoast_head_json.schema
  const breadcrumbs = find(schema['@graph'], {
    '@type': 'BreadcrumbList'
  })?.itemListElement

  pageData.type === 'post' &&
    breadcrumbs.splice(
      1,
      1,
      ...[
        {
          '@type': 'ListItem',
          item: '/newsroom/',
          name: 'Newsroom',
          position: 2
        },
        {
          '@type': 'ListItem',
          // item: '/newsroom/news/',
          name: 'News',
          position: 3
        }
      ]
    )

  return breadcrumbs
}

export const normalizeHeroTitleSection = async (
  section: any,
  pageData: any
) => ({
  breadcrumbs: normalizeYoastGraph(pageData),
  ...section
})

export const resolveGravityFieldsFromFormId = async (section: any) => {
  const {
    data: { fields, button }
  } = await axios.get(`${API.GRAVITY_FORM}/${section.form_id}/`)

  return {
    ...section,
    form: {
      ...section.form,
      submit: button.text,
      fields
    }
  }
}

export const mapPageStaticProps = async ({ acf, ...pageData }: WPPageData) => {
  return {
    ...pageData,
    acf: {
      ...(acf?.form_id ? await resolveGravityFieldsFromFormId(acf) : acf),
      builder: await Promise.all(
        map(acf?.builder, async (section) =>
          section.acf_fc_layout === 'form'
            ? resolveGravityFieldsFromFormId(section)
            : section
        )
      )
    }
  }
}

export const mapOptionsDataProps = async (optionsData: any) => {
  const formData = await new Promise<{ fields: any[] }>(
    (resolve) =>
      optionsData.newsletter_form_id &&
      axios
        .get(`${API.GRAVITY_FORM}/${optionsData.newsletter_form_id}/`)
        .then(({ data }) => resolve(data))
  )

  return {
    ...optionsData,
    fields: formData?.fields
  }
}

export const mapArchiveStaticProps = (pageData: WPPageData) => {
  return {
    ...pageData,
    acf: {
      ...pageData.acf,
      builder: pageData.posts.flatMap((section, index, array) => {
        const textOnImage = {
          acf_fc_layout: index === 0 ? 'hero_resize' : 'text_on_image',
          image:
            index === 0
              ? section.featured_image
              : section.acf.hero.poster?.desktop.url
                ? section.acf.hero.poster.desktop
                : section.featured_image,
          pretitle: section.acf.hero.headline,
          title: section.title.rendered,
          text: '',
          link: {
            url: section.link
          },
          theme: 'dark',
          alignment: index === 0 ? 'left' : index % 2 === 0 ? 'left' : 'right',
          is_boxed: index !== 0,
          use_square_layout: false
        }

        if (index < array.length + 1) {
          return [
            textOnImage,
            {
              acf_fc_layout: 'spacer',
              variant: index === array.length - 1 ? 'md' : 'xs',
              theme: 'dark'
            }
          ]
        }

        return [textOnImage]
      })
    }
  }
}
